











































import { Vue, Prop, Component, Watch } from 'vue-property-decorator'
import { LayerPattern, PalletPattern } from '@/model'

@Component
export default class CancelValidationDialog extends Vue {
  @Prop({ default: false }) enable!: boolean
  @Prop() private text!: string
  @Prop({ default: null }) private palletPattern!: PalletPattern | null
  @Prop({ default: null }) private progress!: any
  @Prop({ default: 2 }) private secondsPerBox!: number
  private dialog: boolean = this.enable
  private elapsedSeconds: number = 0
  private progressStartedAt: number = 0
  private progressTimer: any = null

  @Watch('enable')
  enableWatcher (val: boolean) {
    this.dialog = val
    if (val) {
      this.startProgress()
    } else {
      this.stopProgress()
    }
  }

  getText() {
    if (this.text == null || this.text.length < 1) {
      return this.$t('general.loading').toString()
    }
    return this.text
  }

  get boxCount() {
    const totalBoxes = this.progress && Number(this.progress.totalBoxes)
    if (Number.isFinite(totalBoxes) && totalBoxes > 0) {
      return totalBoxes
    }
    const pallet = this.palletPattern && this.palletPattern.pallet
    const layerPatterns = pallet && pallet.layerPatterns ? pallet.layerPatterns : []
    const layers = pallet && pallet.layers ? pallet.layers : []
    return layerPatterns.reduce((total: number, layerPattern: LayerPattern) => {
      const boxes = (layerPattern.boxPositions || []).length + (layerPattern.boxPositionsAlt || []).length
      const layersWithPattern = layers.filter(layer => layer.layerPattern === layerPattern.name).length
      return total + boxes * layersWithPattern
    }, 0)
  }

  get hasProgress() {
    return this.boxCount > 0
  }

  get currentBox() {
    if (!this.hasProgress) {
      return 0
    }
    return Math.min(this.boxCount, Math.max(1, Math.floor(this.progressBox)))
  }

  get platformCurrentBox() {
    const box = this.progress && Number(this.progress.currentBox)
    if (!Number.isFinite(box) || box < 1) {
      return 0
    }
    return Math.min(this.boxCount, box)
  }

  get hasPlatformProgress() {
    return this.platformCurrentBox > 0
  }

  get progressBox() {
    if (!this.hasProgress) {
      return 0
    }
    if (!this.hasPlatformProgress) {
      return Math.min(this.maxProgressBox, this.boxCount * 0.05)
    }
    if (this.platformCurrentBox >= this.boxCount) {
      return this.maxProgressBox
    }
    return Math.min(this.maxProgressBox, this.platformCurrentBox)
  }

  get maxProgressBox() {
    return this.boxCount > 1 ? this.boxCount - 1 : 0.95
  }

  get progressValue() {
    if (!this.hasProgress) {
      return 0
    }
    return Math.min(98, this.progressBox / this.boxCount * 100)
  }

  get progressPercent() {
    return Math.round(this.progressValue)
  }

  get progressText() {
    return this.$t('general.validationProgressBoxes', [String(this.currentBox), String(this.boxCount)]).toString()
  }

  get remainingSeconds() {
    if (!this.hasProgress || this.progressBox >= this.boxCount) {
      return 0
    }
    return Math.max(1, Math.ceil((this.boxCount - this.progressBox) * this.estimatedSecondsPerBox))
  }

  get estimatedSecondsPerBox() {
    if (!this.hasPlatformProgress) {
      return this.normalizedSecondsPerBox
    }
    const elapsedAtProgress = this.progressTimestamp > 0
      ? Math.max(0, (this.progressTimestamp - this.progressStartedAt) / 1000)
      : this.elapsedSeconds
    return elapsedAtProgress > 0
      ? Math.max(1, elapsedAtProgress / this.platformCurrentBox)
      : this.normalizedSecondsPerBox
  }

  get progressTimestamp() {
    const timestamp = this.progress && Number(this.progress.receivedAt || this.progress.timestamp)
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0
  }

  get normalizedSecondsPerBox() {
    return Math.max(1, this.secondsPerBox || 1)
  }

  private mounted () {
    if (this.enable) {
      this.startProgress()
    }
  }

  private beforeDestroy () {
    this.stopProgress()
  }

  private startProgress () {
    this.stopProgress()
    this.elapsedSeconds = 0
    this.progressStartedAt = Date.now()
    this.progressTimer = window.setInterval(() => {
      this.elapsedSeconds = (Date.now() - this.progressStartedAt) / 1000
    }, 250)
  }

  private stopProgress () {
    if (this.progressTimer != null) {
      window.clearInterval(this.progressTimer)
      this.progressTimer = null
    }
  }

  private formatDuration(seconds: number) {
    const totalSeconds = Math.max(0, Math.round(seconds))
    const minutes = Math.floor(totalSeconds / 60)
    const rest = totalSeconds % 60
    return minutes > 0 ? `${minutes}m ${String(rest).padStart(2, '0')}s` : `${rest}s`
  }

  private cancel() {
    this.$emit('cancel')
  }
}
